/**
 * SHER-462 — Excel/CSV export for the surgical waiting list ("Liste chirurgicale").
 *
 * Surgeons use the exported table as a record of surgical activity, so the CIM-11
 * diagnosis must be exported IN FULL — never the truncated "Arthrose du genou…"
 * cell shown in the table. The file is a UTF-8 CSV (opens directly in Excel); the
 * client has no xlsx library and this reuses the existing blob-download pattern
 * (see BillablesConfiguration.tsx) with zero new dependencies.
 */

import { ExportColumn, buildTableCsv } from '../../../utils/csvDownload';

export interface SurgicalListRow {
    nhc: string;
    patient: string;
    dob: string;
    mainSurgeon: string;
    department: string;
    service: string;
    /** Full, untruncated CIM-11 diagnosis (formatSurgeryCim11 output, "-" when none). */
    cim11Full: string;
    dateAdded: string;
    dateCompleted: string;
    totalDays: string;
    status: string;
}

export interface SurgicalListLabels {
    nhc: string;
    patient: string;
    dob: string;
    mainSurgeon: string;
    department: string;
    service: string;
    cim11: string;
    dateAdded: string;
    dateCompleted: string;
    totalDays: string;
    status: string;
}

/**
 * Build the surgical-list CSV. Patient identity columns (health id, name, DOB)
 * are included only for the global waiting list view (`includePatientColumns`),
 * mirroring the table which splices them in when not scoped to a single patient.
 */
export function buildSurgicalListCsv(
    rows: SurgicalListRow[],
    labels: SurgicalListLabels,
    includePatientColumns: boolean,
): string {
    const patientColumns: ExportColumn<SurgicalListRow>[] = [
        { header: labels.nhc, value: (row) => row.nhc },
        { header: labels.patient, value: (row) => row.patient },
        { header: labels.dob, value: (row) => row.dob },
    ];
    const clinicalColumns: ExportColumn<SurgicalListRow>[] = [
        { header: labels.mainSurgeon, value: (row) => row.mainSurgeon },
        { header: labels.department, value: (row) => row.department },
        { header: labels.service, value: (row) => row.service },
        { header: labels.cim11, value: (row) => row.cim11Full },
        { header: labels.dateAdded, value: (row) => row.dateAdded },
        { header: labels.dateCompleted, value: (row) => row.dateCompleted },
        { header: labels.totalDays, value: (row) => row.totalDays },
        { header: labels.status, value: (row) => row.status },
    ];
    const columns = includePatientColumns ? [...patientColumns, ...clinicalColumns] : clinicalColumns;

    // The column model, the header/cell zip, the escaping, the CRLF and the Excel-facing
    // UTF-8 BOM all come from the shared primitive (SHER-905). This file used to carry its
    // own `Array<[string, (row) => string]>` tuples and its own zip, which was `ExportColumn`
    // with different punctuation — one idea spelled two ways, in the two exports the app has.
    return buildTableCsv(rows, columns);
}
